// feature types
import type { JSONSchema7 } from 'json-schema'
import { HomepageJumpPages } from 'types/navigation'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import {
  BusinessValueOption,
  DEFAULT_BUSINESS_VALUE,
} from 'utils/fde/aiaFinancialConsequenceHelpers'
import { parseBoolean, parseNumber, parseString } from 'utils/parsingHelpers'

import { CaseStatus, CaseType } from './geckoCase'
import { GeckoEntryBase } from './geckoEntry'

export const HealthPlanType = {
  Default: 'default',
  Mine: 'mine',
  Rooftops: 'rooftops',
} as const

export type HealthPlanType =
  (typeof HealthPlanType)[keyof typeof HealthPlanType]

export const AssetLifecycleConditionView = {
  Overview: 'overview',
  Risk: 'risk',
  NextRecommendedAction: 'next recommended action',
} as const

export type AssetLifecycleConditionView =
  (typeof AssetLifecycleConditionView)[keyof typeof AssetLifecycleConditionView]

/**
 * Language that generated case and alert narratives are written in at a site.
 * Stored as `data.language`; when unset, generated narratives default to English.
 */
export const SITE_LANGUAGES = ['en', 'es'] as const

export type SiteLanguage = (typeof SITE_LANGUAGES)[number]

export const isSiteLanguage = (value: unknown): value is SiteLanguage =>
  SITE_LANGUAGES.some((language) => language === value)

const parseSiteLanguage = (input: unknown): SiteLanguage | undefined =>
  isSiteLanguage(input) ? input : undefined

export enum FacilityGraphSourceKind {
  ROLODEX = 'rolodex',
  NEO4J = 'neo4j',
}

const parseFacilityGraphSource = (input: unknown): FacilityGraphSourceKind =>
  Object.values(FacilityGraphSourceKind).find((source) => source === input) ??
  FacilityGraphSourceKind.ROLODEX

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- this is the actual type
const isNullOrUndefined = (value: any) => {
  return value === null || value === undefined
}

/**
 * Defaults for the ALM Asset Details page's section visibility flags, exported
 * because consumers need them too: these fields are only populated when a
 * site-config entry exists, so callers must apply the same default when the site
 * has no entry (or while the query is still in flight).
 *
 * Plans and Streetview are opt-*out* — unlike every other boolean here — because
 * they gate sections that already render for every ALM site, so an absent key
 * must not change behavior on deploy. Tickets is opt-in: the section is new.
 */
export const DEFAULT_SHOW_ALM_ASSET_DETAILS_PLANS_SECTION = true
export const DEFAULT_SHOW_ALM_ASSET_DETAILS_STREETVIEW_SECTION = true
export const DEFAULT_SHOW_ALM_ASSET_DETAILS_TICKETS_SECTION = false

export class GeckoSiteConfig extends GeckoEntryBase<
  {
    budget: number
    chartTimeframe: number
    site_level_criticality_mapping?: Record<string, string>
    yearlyBudgets: Record<string, number>
    businessValue?: BusinessValueOption
    healthPlanType?: HealthPlanType
    homepageJumpPages?: HomepageJumpPages[]
    includeDeficiencies?: boolean
    showDocumentOcrBoxes?: boolean
    enableCmmsExplorer: boolean
    includeRiskScenarios?: boolean
    caseConfig?: {
      caseStatuses: CaseStatus[]
      caseTypes: CaseType[]
      caseMetadataSchema?: JSONSchema7
    }
    canAutoLaunchPipeline: boolean
    showCapexOutlookOnSiteOverview?: boolean
    useAssetLifecycle?: boolean
    assetLifecycleConditionView?: AssetLifecycleConditionView
    showCorrosionMetrics?: boolean
    enableAlmRepairPlanning?: boolean
    enableBookkeepingReview?: boolean
    showAlmAssetDetailsPlansSection: boolean
    showAlmAssetDetailsStreetviewSection: boolean
    showAlmAssetDetailsTicketsSection: boolean
    language?: SiteLanguage
    facilityGraphSource: FacilityGraphSourceKind
  },
  {
    site: string
    organization: string
  }
> {
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        budget: parseNumber(json.data.budget) ?? 0,
        chartTimeframe: parseNumber(json.data.chart_timeframe) ?? 0,
        site_level_criticality_mapping: isNullOrUndefined(
          json.data.site_level_criticality_mapping
        )
          ? {
              '1': '1',
              '2': '2',
              '3': '3',
              '4': '4',
              '5': '5',
            }
          : (json.data.site_level_criticality_mapping as Record<
              string,
              string
            >),
        yearlyBudgets: isNullOrUndefined(json.data.yearly_budgets)
          ? {}
          : (json.data.yearly_budgets as Record<string, number>),
        businessValue: (parseString(json.data.business_value) ??
          DEFAULT_BUSINESS_VALUE) as BusinessValueOption,
        healthPlanType: (parseString(json.data.health_plan_type) ??
          HealthPlanType.Default) as HealthPlanType,
        homepageJumpPages: isNullOrUndefined(json.data.homepage_jump_pages)
          ? []
          : (json.data.homepage_jump_pages as HomepageJumpPages[]),
        includeDeficiencies: isNullOrUndefined(json.data.include_deficiencies)
          ? false
          : (json.data.include_deficiencies as boolean),
        showDocumentOcrBoxes:
          parseBoolean(json.data.show_document_ocr_boxes) ?? false,
        enableCmmsExplorer:
          parseBoolean(json.data.enable_cmms_explorer) ?? false,
        includeRiskScenarios: isNullOrUndefined(
          json.data.include_risk_scenarios
        )
          ? false
          : (json.data.include_risk_scenarios as boolean),
        caseConfig: isNullOrUndefined(json.data.case_config)
          ? undefined
          : (json.data.case_config as {
              caseStatuses: CaseStatus[]
              caseTypes: CaseType[]
              caseMetadataSchema?: JSONSchema7 // Used for defining the schema of case metadata for the site
            }),
        showCapexOutlookOnSiteOverview: isNullOrUndefined(
          json.data.show_capex_outlook_on_site_overview
        )
          ? false
          : (json.data.show_capex_outlook_on_site_overview as boolean),
        useAssetLifecycle: isNullOrUndefined(json.data.use_asset_lifecycle)
          ? false
          : (json.data.use_asset_lifecycle as boolean),
        assetLifecycleConditionView: isNullOrUndefined(
          json.data.asset_lifecycle_condition_view
        )
          ? AssetLifecycleConditionView.Overview
          : (json.data
              .asset_lifecycle_condition_view as AssetLifecycleConditionView),
        showCorrosionMetrics: isNullOrUndefined(
          json.data.show_corrosion_metrics
        )
          ? false
          : (json.data.show_corrosion_metrics as boolean),
        enableAlmRepairPlanning: isNullOrUndefined(
          json.data.enable_alm_repair_planning
        )
          ? false
          : (json.data.enable_alm_repair_planning as boolean),
        enableBookkeepingReview: isNullOrUndefined(
          json.data.enable_bookkeeping_review
        )
          ? false
          : (json.data.enable_bookkeeping_review as boolean),
        canAutoLaunchPipeline:
          parseBoolean(json.data.can_auto_launch_pipeline) ?? false,
        showAlmAssetDetailsPlansSection:
          parseBoolean(json.data.show_alm_asset_details_plans_section) ??
          DEFAULT_SHOW_ALM_ASSET_DETAILS_PLANS_SECTION,
        showAlmAssetDetailsStreetviewSection:
          parseBoolean(json.data.show_alm_asset_details_streetview_section) ??
          DEFAULT_SHOW_ALM_ASSET_DETAILS_STREETVIEW_SECTION,
        showAlmAssetDetailsTicketsSection:
          parseBoolean(json.data.show_alm_asset_details_tickets_section) ??
          DEFAULT_SHOW_ALM_ASSET_DETAILS_TICKETS_SECTION,
        language: parseSiteLanguage(json.data.language),
        facilityGraphSource: parseFacilityGraphSource(
          json.data.facility_graph_source
        ),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
      },
      {}
    )
  }

  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoSiteConfig(json)
  }
}
